import { CheckCircle, Mail, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequestMagicLink, useVerifyCode } from '../../hooks/api/useAuth';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  redirectOnCancel?: string; // Where to go if user cancels (default: '/')
  title?: string;
  description?: string;
}

export function LoginModal({
  isOpen,
  onClose,
  redirectOnCancel = '/',
  title = 'Sign in to continue',
  description = 'Enter your email to receive a magic sign-in link',
}: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const requestMagicLink = useRequestMagicLink();
  const verifyCode = useVerifyCode();
  const navigate = useNavigate();
  const emailInputId = useId();
  const codeInputId = useId();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) return;

    requestMagicLink.mutate(
      { email, redirectTo: window.location.pathname + window.location.search },
      {
        onSuccess: (data) => {
          setSubmitted(true);

          // In dev mode, show the code
          if (data?.devCode && window.location.hostname === 'localhost') {
            console.log('🔢 Dev verification code:', data.devCode);
            setDevCode(data.devCode);
          }
        },
      },
    );
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || !email) return;

    verifyCode.mutate(
      { email, code },
      {
        onSuccess: () => {
          // Tokens are now in localStorage, just reload the current page
          // The ProtectedRoute will see isAuthenticated=true and show content
          // Don't call handleClose() here as it might trigger navigation before reload
          onClose(); // Just close the modal
          window.location.reload(); // Reload to show authenticated content
        },
      },
    );
  };

  const handleResend = () => {
    setCode('');
    setDevCode(null);
    verifyCode.reset();
    requestMagicLink.mutate(
      { email, redirectTo: window.location.pathname + window.location.search },
      {
        onSuccess: (data) => {
          if (data?.devCode && window.location.hostname === 'localhost') {
            console.log('🔢 Dev verification code:', data.devCode);
            setDevCode(data.devCode);
          }
        },
      },
    );
  };

  const handleClose = () => {
    setSubmitted(false);
    setEmail('');
    setCode('');
    setDevCode(null);
    requestMagicLink.reset();
    verifyCode.reset();
    onClose();

    // User canceled without logging in, redirect to fallback
    if (redirectOnCancel) {
      navigate(redirectOnCancel);
    }
  };

  // Auto-fill code in dev mode
  useEffect(() => {
    if (devCode && window.location.hostname === 'localhost') {
      setCode(devCode);
    }
  }, [devCode]);

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <DialogTitle className="text-center">Enter verification code</DialogTitle>
            <DialogDescription className="text-center">
              We've sent a 6-digit code to <strong>{email}</strong>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleVerifyCode} className="space-y-4">
            {verifyCode.error && (
              <Alert variant="destructive">
                <AlertDescription>
                  {verifyCode.error.message || 'Invalid code. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            {devCode && window.location.hostname === 'localhost' && (
              <Alert>
                <AlertDescription>
                  <strong>Dev mode:</strong> Code auto-filled: {devCode}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor={codeInputId}>Verification Code</Label>
              <Input
                id={codeInputId}
                type="text"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                disabled={verifyCode.isPending}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>

            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                Check your email for the 6-digit code. It will expire in 15 minutes.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                className="w-full"
                disabled={code.length !== 6 || verifyCode.isPending}
              >
                {verifyCode.isPending ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={requestMagicLink.isPending}
                  className="text-sm text-primary underline hover:no-underline disabled:opacity-50"
                >
                  {requestMagicLink.isPending ? 'Sending...' : 'Resend code'}
                </button>

                <Button variant="ghost" size="sm" onClick={handleClose}>
                  <X className="h-4 w-4 mr-1" />
                  Close
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {requestMagicLink.error && (
            <Alert variant="destructive">
              <AlertDescription>
                {requestMagicLink.error.message || 'Failed to send magic link. Please try again.'}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor={emailInputId}>Email</Label>
            <Input
              id={emailInputId}
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={requestMagicLink.isPending}
            />
          </div>

          <div className="flex justify-between gap-3">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>

            <Button
              type="submit"
              className="flex-1"
              disabled={!email || requestMagicLink.isPending}
            >
              {requestMagicLink.isPending ? (
                <>
                  <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Link
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
